import { Entity } from '../entity.js';
import { EFFECT_TYPES } from '../combat/statusEffects.js';
import {
  abilityWorldScale,
  blinkToward,
  clampGroundCast,
  isInAbilityCircle,
  isWithinAbilityRange
} from '../combat/abilityArea.js';
import {
  CAITLYN_W_DURATION_SEC,
  CAITLYN_W_EFFECT_DELAY_SEC,
  CAITLYN_W_MANA,
  CAITLYN_W_RANGE,
  CAITLYN_W_ROOT_SEC,
  CAITLYN_W_TRAP_RADIUS
} from '../abilities/params/caitlynParams.js';

export {
  CAITLYN_W_DURATION_SEC,
  CAITLYN_W_TRAP_RADIUS,
  CAITLYN_W_ROOT_SEC
} from '../abilities/params/caitlynParams.js';

function pushSkillshot(bucket, caster, tx, ty, gameTime, opts) {
  const angle = Math.atan2(ty - caster.y, tx - caster.x);
  bucket.push({
    id: `${opts.idPrefix}-${caster.id}-${gameTime}`,
    casterId: caster.id,
    team: caster.team,
    x: caster.x,
    y: caster.y,
    vx: Math.cos(angle) * opts.speed,
    vy: Math.sin(angle) * opts.speed,
    range: opts.range,
    traveled: 0,
    damage: opts.damage,
    damageType: opts.damageType || 'physical',
    radius: opts.radius || 16
  });
}

export function createCaitlyn(id = 'caitlyn-1', team = 'blue', x = 300, y = 1300) {
  const cait = new Entity({
    id,
    type: 'champion',
    team,
    x,
    y,
    maxHp: 540,
    maxMp: 315,
    ad: 59,
    ap: 0,
    armor: 26,
    mr: 30,
    ms: 325,
    attackRange: 650,
    attackSpeed: 0.625,
    name: 'Caitlyn',
    radius: 20
  });
  cait.yordleTrap = null;
  return cait;
}

export function castCaitlynQ(caster, tx, ty, projectiles, gameTime) {
  if (!caster || caster.isDead || caster.mp < 55) return false;
  caster.mp -= 55;
  pushSkillshot(projectiles, caster, tx, ty, gameTime, {
    idPrefix: 'caitlyn-q',
    speed: 2200,
    range: 1300,
    damage: 50 + (caster.ad || 0) * 1.3,
    radius: 60
  });
  return true;
}

export function castCaitlynW(caster, tx, ty) {
  if (!caster || caster.isDead || caster.mp < CAITLYN_W_MANA) return false;
  caster.mp -= CAITLYN_W_MANA;
  const aim = clampGroundCast(caster, tx, ty, CAITLYN_W_RANGE);
  const live = Number.isFinite(caster.liveWorldScale);
  caster.yordleTrap = {
    x: aim.x,
    y: aim.y,
    ...(live ? { z: aim.y } : {}),
    scale: abilityWorldScale(caster),
    useSandboxZ: live,
    armSec: CAITLYN_W_EFFECT_DELAY_SEC,
    remainingSec: CAITLYN_W_DURATION_SEC
  };
  return true;
}

export function tickCaitlynYordleTrap(caster, entities = [], dt = 0) {
  const trap = caster?.yordleTrap;
  if (!trap) return null;
  if (trap.armSec > 0) {
    trap.armSec -= dt;
    if (trap.armSec > 0) return trap;
  }
  trap.remainingSec -= dt;
  if (trap.remainingSec <= 0) {
    caster.yordleTrap = null;
    return null;
  }
  const scaleCaster = { liveWorldScale: trap.scale || 1 };
  const oy = Number.isFinite(trap.z) ? trap.z : trap.y;
  for (const e of entities) {
    if (!e || e.isDead || e.team === caster.team) continue;
    const probe = trap.useSandboxZ
      ? { x: e.x, y: Number.isFinite(e.z) ? e.z : e.y }
      : e;
    if (!isInAbilityCircle(probe, trap.x, oy, CAITLYN_W_TRAP_RADIUS, scaleCaster)) continue;
    e.statusManager?.addEffect?.(EFFECT_TYPES.ROOT, CAITLYN_W_ROOT_SEC, 0);
    caster.yordleTrap = null;
    return null;
  }
  return trap;
}

export function castCaitlynE(caster, tx, ty, projectiles, gameTime) {
  if (!caster || caster.isDead || caster.mp < 75) return false;
  caster.mp -= 75;
  const angle = Math.atan2(ty - caster.y, tx - caster.x);
  const dirX = -Math.cos(angle);
  const dirY = -Math.sin(angle);
  blinkToward(caster, caster.x + dirX * 1000, caster.y + dirY * 1000, 390);
  pushSkillshot(projectiles, caster, tx, ty, gameTime, {
    idPrefix: 'caitlyn-e',
    speed: 1600,
    range: 800,
    damage: 70 + (caster.ad || 0) * 0.8,
    radius: 14
  });
  return true;
}

export function castCaitlynR(caster, target) {
  if (!caster || caster.isDead || !target || target.isDead) return false;
  if (target.team === caster.team || caster.mp < 100) return false;
  if (!isWithinAbilityRange(caster, target, 3500, caster)) return false;
  caster.mp -= 100;
  const dmg = 250 + (caster.ad || 0) * 2;
  target.takeDamage(dmg, caster, 'physical');
  return true;
}
